/**
 * =============================================================================
 * Página: AffiliateInviteResolver
 * Rota:   /afiliado/convite/:token
 * Versão: 1.0.0
 *
 * Descrição:
 *   Resolver invisível de convite. Ao acessar o link do e-mail:
 *   1. Valida o token via RPC pública `validate_affiliate_program_invite`.
 *   2. Se válido e o e-mail já tem conta afiliado no tenant, redireciona
 *      para /afiliado/login?next=/afiliado/aceitar-convite/:token
 *   3. Se válido e é e-mail novo, redireciona para a tela de cadastro do
 *      programa com o token na query string.
 *   4. Se inválido/expirado, mostra mensagem clara.
 *
 * Invariante: apenas leitura + redirect. Nenhuma escrita nesta página.
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { setLastAffiliateTenantSlug } from '@/lib/affiliate-urls';

interface ValidationRow {
  valid: boolean;
  reason: string | null;
  program_id: string | null;
  program_name: string | null;
  program_slug: string | null;
  tenant_slug: string | null;
  tenant_name: string | null;
  email: string | null;
  has_affiliate_account: boolean | null;
}

export default function AffiliateInviteResolver() {
  const { token = '', tenantSlug: tenantSlugParam } = useParams<{ token: string; tenantSlug?: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Link de convite inválido.');
      return;
    }

    (async () => {
      const { data, error: rpcErr } = await supabase.rpc(
        'validate_affiliate_program_invite',
        { p_token: token } as any,
      );

      if (rpcErr) {
        setError('Não foi possível validar o convite. Tente novamente.');
        return;
      }

      const row = (data as ValidationRow[] | null)?.[0];
      if (!row || !row.valid) {
        const reason = row?.reason ?? 'invalid';
        if (reason === 'expired') setError('Este convite expirou.');
        else if (reason === 'already_accepted')
          setError('Este convite já foi aceito.');
        else setError('Convite inválido ou revogado.');
        return;
      }

      // Convite válido — decide rota conforme existência de conta afiliado
      const tenantSlug = row.tenant_slug ?? '';
      const programSlug = row.program_slug ?? '';

      // Persiste o tenant para manter URLs canônicas em telas subsequentes
      // (login/cadastro/reset). Usa o slug da rota quando disponível, senão
      // o retornado pela RPC.
      setLastAffiliateTenantSlug(tenantSlugParam || tenantSlug || null);

      if (row.has_affiliate_account) {
        // Já é afiliado do tenant → login + aceite automático (rota canônica)
        const acceptPath = `/afiliado/${tenantSlug}/aceitar-convite/${token}`;
        navigate(
          `/afiliado/${tenantSlug}/login?next=${encodeURIComponent(acceptPath)}`,
          { replace: true },
        );
      } else {
        // E-mail novo → cadastro com token na query string
        navigate(
          `/afiliado/cadastro/${tenantSlug}/${programSlug}?invite=${token}`,
          { replace: true },
        );
      }
    })();
  }, [token, tenantSlugParam, navigate]);

  if (error) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center p-6 bg-background">
        <div className="max-w-md w-full text-center space-y-4 border rounded-2xl p-8">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center">
            <AlertCircle className="h-7 w-7" />
          </div>
          <h1 className="text-xl font-semibold">Convite indisponível</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button onClick={() => navigate('/afiliado/login', { replace: true })}>
            Ir para login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-background">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Validando convite…</span>
      </div>
    </div>
  );
}
